import { ceil, toInteger } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import { useRef } from 'react';
import { Message } from '@meitu/saas-ui';
import { getFramework, getTargetType, useIdentity, useMoveEventListener } from '@/framework';
import { getLocaleStore } from '@/i18n';
import { ITouchKitEvent } from '@/utils/event-kit';
import { ILayerProps, MAX_STAGE_WIDTH as MAX_STAGE_SIZE, MIN_STAGE_WIDTH as MIN_STAGE_SIZE } from '@/version-types';
import type { CropStagePlugin } from '../controller';
import { useStageCropStore } from '../models/crop-stage-state';
import { getOperateStageStyle, setScaleStageStyle } from '../utils';

const dataTypes = ['crop-bc', 'crop-rc', 'crop-lc', 'crop-tc'];

// 不能超过最大值和最小值
const adjustSize = (size: number, notifySizeLimit: () => void) => {
    if (size < MIN_STAGE_SIZE) {
        notifySizeLimit();
        return 50;
    } else if (size > MAX_STAGE_SIZE) {
        notifySizeLimit();
        return MAX_STAGE_SIZE;
    }

    return size;
};

/**
 * 在图片编辑裁剪舞台挂载边中点宽高调整手势，依赖当前画框及裁剪比例状态
 *
 * 更新尺寸预览，手势结束时提交图层；每次手势超限最多提示一次。
 *
 * @param scale - 裁剪舞台的视口缩放比例
 */
export const useSizeChangeOperate = (scale: number) => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const originIndex = framework.service.frame.getActiveFrameId();
    const getLayers = () => framework.service.frame.getLayers(originIndex);
    const getMainLayer = () => framework.service.frame.getMainLayer(originIndex);
    const cropController = framework.service.registration.getInstance<CropStagePlugin>('CropStagePlugin');

    const { $t } = getLocaleStore();

    const { isStageAutoCrop, setIsCropStageOperating, setOperateBaseInfo } = useStageCropStore()((state) => state);
    const lastMainLayerRef = useRef<ILayerProps | null>(null);
    const lastLayersRef = useRef<ILayerProps[] | null>(null);
    const hasShownSizeLimitRef = useRef(false);
    const notifySizeLimit = () => {
        if (hasShownSizeLimitRef.current) return;
        hasShownSizeLimitRef.current = true;
        Message.error($t((v) => v.stageManager.canvasLimitScope));
    };

    useMoveEventListener((e: ITouchKitEvent) => {
        const target = e.target;
        const dataType = target.getAttribute('data-type');

        if (!getTargetType(target)) return;

        if (!dataTypes.includes(dataType || '')) return;

        if (e.isStart) {
            hasShownSizeLimitRef.current = false;
            const mainLayer = getMainLayer();
            const layers = getLayers();
            lastMainLayerRef.current = cloneDeep(mainLayer);
            lastLayersRef.current = cloneDeep(layers);
            cropController.emitPreviewStageSizeChange(
                mainLayer ? { width: mainLayer.width, height: mainLayer.height } : null,
            );
            return;
        }

        if (!lastMainLayerRef.current || !lastLayersRef.current || !dataType) {
            return;
        }
        setIsCropStageOperating(true);

        const { left, top, width, height } = lastMainLayerRef.current;
        const params = {
            width,
            height,
            left,
            top,
        };

        const { displacementX, displacementY } = e;
        const moveDistance = {
            x: toInteger(displacementX / scale),
            y: toInteger(displacementY / scale),
        };

        if (dataType === 'crop-bc') {
            params.height = adjustSize(toInteger(params.height + moveDistance.y), notifySizeLimit);
        } else if (dataType === 'crop-rc') {
            params.width = adjustSize(toInteger(params.width + moveDistance.x), notifySizeLimit);
        } else if (dataType === 'crop-lc') {
            const val = toInteger(params.width - moveDistance.x);
            params.width = ceil(adjustSize(val, notifySizeLimit));

            params.left = params.left - ceil(moveDistance.x - (params.width - val));
        } else if (dataType === 'crop-tc') {
            const val = toInteger(params.height - moveDistance.y);
            params.height = ceil(adjustSize(val, notifySizeLimit));

            params.top = params.top - ceil(moveDistance.y - (params.height - val));
        }

        // 固定比例裁剪的时候拖动也要固定比例
        if ((dataType === 'crop-rc' || dataType === 'crop-lc') && !isStageAutoCrop) {
            params.height = (params.width / width) * height;
            params.top = params.top - (height - params.height) / 2;

            if (params.height > MAX_STAGE_SIZE) {
                notifySizeLimit();
                params.top = params.top - (params.height - MAX_STAGE_SIZE) / 2;
                params.height = MAX_STAGE_SIZE;
                params.left = params.left - (params.width - (params.height / height) * width);
                params.width = (params.height / height) * width;
            }

            if (params.height < MIN_STAGE_SIZE) {
                notifySizeLimit();
                params.top = params.top - (params.height - MIN_STAGE_SIZE) / 2;
                params.height = MIN_STAGE_SIZE;
                params.left = params.left - (params.width - (params.height / height) * width);
                params.width = (params.height / height) * width;
            }
        }

        if ((dataType === 'crop-bc' || dataType === 'crop-tc') && !isStageAutoCrop) {
            params.width = (params.height / height) * width;
            params.left = params.left - (width - params.width) / 2;

            if (params.width > MAX_STAGE_SIZE) {
                notifySizeLimit();
                params.left = params.left - (params.width - MAX_STAGE_SIZE) / 2;
                params.width = MAX_STAGE_SIZE;
                params.top = params.top - (params.height - (params.width / width) * height);
                params.height = (params.width / width) * height;
            }

            if (params.height < MIN_STAGE_SIZE) {
                notifySizeLimit();
                params.top = params.top - (params.height - MIN_STAGE_SIZE) / 2;
                params.height = MIN_STAGE_SIZE;
                params.left = params.left - (params.width - (params.height / height) * width);
                params.width = (params.height / height) * width;
            }
        }

        const newLayerSize = cropController.moveChangeStageSizeOperate(
            params,
            cloneDeep(lastMainLayerRef.current),
            cloneDeep(lastLayersRef.current),
            dataType,
            isStageAutoCrop,
            e.isEnd,
        );

        if (newLayerSize) {
            if (!e.isEnd) {
                cropController.emitPreviewStageSizeChange({ width: newLayerSize.width, height: newLayerSize.height });
            }
            const nextScale = e.isEnd
                ? (cropController.syncCropFrameViewport(originIndex, newLayerSize) ?? scale)
                : scale;
            const originStageSize = e.isEnd ? newLayerSize : { width, height };
            const moveDirection = {
                x: e.displacementX ? e.displacementX / Math.abs(e.displacementX) : 1,
                y: e.displacementY ? e.displacementY / Math.abs(e.displacementY) : 1,
            };
            setScaleStageStyle(
                newLayerSize.width,
                newLayerSize.height,
                nextScale,
                dataType,
                isStageAutoCrop,
                moveDirection,
                originStageSize,
                e.isEnd,
            );
            const nextOperateBaseInfo = getOperateStageStyle(
                newLayerSize.width,
                newLayerSize.height,
                nextScale,
                dataType,
                isStageAutoCrop,
                moveDirection,
                originStageSize,
                e.isEnd,
            );
            if (nextOperateBaseInfo) {
                setOperateBaseInfo(nextOperateBaseInfo);
            }
        }
        if (e.isEnd) {
            lastMainLayerRef.current = null;
            lastLayersRef.current = null;
            cropController.emitPreviewStageSizeChange(null);
            setIsCropStageOperating(false);
        }
    });
};
